import { useState, useEffect, useCallback } from "react";

export default function SupabaseDeployStatus({ isDark }: { isDark: boolean }) {
  const [status, setStatus] = useState<"checking" | "ok" | "error">("checking");
  const [message, setMessage] = useState("Checking edge functions...");
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";

  const check = useCallback(async () => {
    setStatus("checking");
    setMessage("Checking edge functions...");

    try {
      const res = await fetch("https://vankfsmglinsfmjijpsc.supabase.co/functions/v1/get-site-content", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZhbmtmc21nbGluc2ZtamlqcHNjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxNjcxNTMsImV4cCI6MjA5Mjc0MzE1M30.gb-rW-VmCkHXDNh1YczxGtr-rbrUJriTbbHaWRNweSc",
        },
      });

      if (res.ok) {
        setStatus("ok");
        setMessage("get-site-content is live and responding.");
      } else {
        const text = await res.text().catch(() => res.statusText);
        setStatus("error");
        setMessage(`get-site-content returned HTTP ${res.status}: ${text}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.toLowerCase().includes("networkerror") || msg.toLowerCase().includes("failed to fetch")) {
        setStatus("error");
        setMessage(
          "Edge function not found on your Supabase project. The functions need to be deployed to your real project (vankfsmglinsfmjijpsc). See instructions below."
        );
      } else {
        setStatus("error");
        setMessage(msg);
      }
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  return (
    <div className={`rounded-2xl border p-5 ${cardBg}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-bold text-sm ${headingColor}`}>
          <i className="ri-server-line text-pink-400 mr-1.5"></i>
          Supabase Sync Status
        </h3>
        <button
          onClick={check}
          className={`text-xs flex items-center gap-1 cursor-pointer transition-colors ${isDark ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
        >
          <i className="ri-refresh-line"></i> Recheck
        </button>
      </div>

      <div className={`flex items-center gap-3 p-3 rounded-xl mb-3 ${
        status === "ok" ? (isDark ? "bg-green-900/20" : "bg-green-50") :
        status === "error" ? (isDark ? "bg-red-900/20" : "bg-red-50") :
        (isDark ? "bg-gray-700/50" : "bg-gray-50")
      }`}>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
          status === "ok" ? "bg-green-100" :
          status === "error" ? "bg-red-100" :
          "bg-gray-100"
        }`}>
          {status === "ok" ? (
            <i className="ri-check-line text-green-600 text-sm"></i>
          ) : status === "error" ? (
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
          ) : (
            <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
          )}
        </div>
        <div>
          <div className={`text-xs font-bold ${
            status === "ok" ? "text-green-600" :
            status === "error" ? "text-red-500" :
            headingColor
          }`}>
            {status === "ok" ? "Connected" : status === "error" ? "Not Connected" : "Checking..."}
          </div>
          <div className={`text-xs ${subColor}`}>{message}</div>
        </div>
      </div>

      {status === "error" && (
        <div className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${headingColor}`}>How to deploy the edge functions to your project:</p>
          <ol className={`text-xs leading-relaxed space-y-2 list-decimal list-inside ${subColor}`}>
            <li>Go to your Supabase Dashboard → <strong className={headingColor}>Edge Functions</strong></li>
            <li>Click <strong className={headingColor}>New Function</strong></li>
            <li>Name it <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>get-site-content</code></li>
            <li>Paste the code from <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>supabase/functions/get-site-content/index.ts</code> in your repo</li>
            <li>Click <strong className={headingColor}>Deploy</strong></li>
            <li>Repeat for <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>save-site-content</code> using <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>supabase/functions/save-site-content/index.ts</code></li>
            <li>Make sure these secrets are set in <strong className={headingColor}>Edge Functions → Secrets</strong>:
              <ul className="list-disc list-inside pl-4 mt-1">
                <li><code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>SUPABASE_URL</code> = <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>https://vankfsmglinsfmjijpsc.supabase.co</code></li>
                <li><code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>SUPABASE_ANON_KEY</code> = your anon key</li>
                <li><code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>SUPABASE_SERVICE_ROLE_KEY</code> = your service role key</li>
              </ul>
            </li>
            <li>Also create the <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>site_content</code> table in the SQL Editor (see project docs)</li>
          </ol>
          <p className={`text-xs mt-3 ${subColor}`}>
            <i className="ri-information-line mr-1"></i>
            The <code className={`px-1 rounded ${isDark ? "bg-gray-700 text-pink-300" : "bg-pink-50 text-pink-600"}`}>upload-image</code> function is already deployed and working — use it as a reference.
          </p>
        </div>
      )}
    </div>
  );
}